import type { Position } from 'components/map/types';
import { z } from 'zod';

import { DeviationSchema } from 'APIzod';

/**
 * Wraps a zod schema with graceful error handling for forward-compatibility.
 * When parsing fails (e.g. an unknown enum value from a future backend version),
 * it logs the error to the console and returns the provided fallback value instead of throwing.
 *
 * @param schema - The zod schema to wrap
 * @param fallback - The value to return when parsing fails, or a factory returning it
 * @param label - Optional label included in the console error for easier debugging
 *
 * @see https://confluence.collaborationlayer-traton.com/spaces/ACC/pages/379102495/Data+availability
 */
export const catchWithLog = <T>(schema: z.ZodType<T>, fallback: T | (() => T), label?: string): z.ZodType<T> => {
    // Resolve lazily so a `() => T` fallback is evaluated at parse time, not once
    // when the schema literal is built at module load. This matters for fallbacks
    // that capture the current time (e.g. a synthetic PARSING_ERROR's timestamp):
    // evaluating eagerly would freeze that timestamp for the whole session.
    const resolveFallback = (): T => (typeof fallback === 'function' ? (fallback as () => T)() : fallback);

    return z.preprocess(
        value => {
            const result = schema.safeParse(value);
            if (!result.success) {
                console.error(`Zod catch triggered${label ? ` (${label})` : ''}`, value, result.error);
                return resolveFallback();
            }
            return value;
        },
        schema.catch(() => resolveFallback())
    ) as z.ZodType<T>;
};

/**
 * {@link catchWithLog} for fields carrying personal data. The console line is the label alone —
 * neither the offending value nor the Zod issue is passed, because a Zod issue echoes its input,
 * and here that input is a person's name, phone number or email address.
 */
export const catchWithRedactedLog = <T>(schema: z.ZodType<T>, fallback: T | (() => T), label: string): z.ZodType<T> => {
    const resolveFallback = (): T => (typeof fallback === 'function' ? (fallback as () => T)() : fallback);

    return z.preprocess(
        value => {
            const result = schema.safeParse(value);
            if (!result.success) {
                console.error(`Zod catch triggered (${label}) — value redacted`);
                return resolveFallback();
            }
            return value;
        },
        schema.catch(() => resolveFallback())
    ) as z.ZodType<T>;
};

export const UuidSchema = () => z.guid();
export const NonEmptyString = () => z.string().min(1);
export const OptionalNonEmptyString = () => z.string().min(1).optional();
export const OptionalNumber = () => z.number().optional();
export const DateTimeStringSchema = () => z.iso.datetime();
export const NonNegativeNumber = () => z.number().min(0);
export const RioPositionSchema = (): z.ZodType<Position> =>
    z.object({
        lat: z.number(),
        lng: z.number(),
    });

export const HEREResultType = z.enum([
    'place',
    'locality',
    'street',
    'houseNumber',
    'administrativeArea',
    'addressBlock',
    'intersection',
    'postalCodePoint',
    'chainQuery',
    'categoryQuery',
]);
export const MapViewSchema = () =>
    z.object({
        west: OptionalNumber(),
        south: OptionalNumber(),
        east: OptionalNumber(),
        north: OptionalNumber(),
    });

export const AddressSchema = () =>
    z.object({
        label: OptionalNonEmptyString(),
        countryCode: OptionalNonEmptyString(),
        countryName: OptionalNonEmptyString(),
        state: OptionalNonEmptyString(),
        county: OptionalNonEmptyString(),
        city: OptionalNonEmptyString(),
        district: OptionalNonEmptyString(),
        street: OptionalNonEmptyString(),
        postalCode: OptionalNonEmptyString(),
        houseNumber: OptionalNonEmptyString(),
    });

export const HEREAddressItemSchema = () =>
    z.object({
        title: OptionalNonEmptyString(),
        id: OptionalNonEmptyString(),
        resultType: HEREResultType.optional(),
        houseNumberType: OptionalNonEmptyString(),
        address: AddressSchema().optional(),
        position: RioPositionSchema(),
        access: z.array(RioPositionSchema()).optional(),
        distance: OptionalNumber(),
        mapView: MapViewSchema().optional(),
    });

export const HereReverseGeocodeResponseSchema = () =>
    z.object({
        items: z.array(HEREAddressItemSchema()).optional(),
    });

export const DeviationWithVINSchema = () =>
    DeviationSchema().extend({
        vin: z.string().min(1),
    });
